import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
	addDoc,
	collection,
	deleteDoc,
	deleteField,
	doc,
	getDoc,
	getDocs,
	limit,
	orderBy,
	query,
	serverTimestamp,
	setDoc,
	updateDoc,
	where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext/AuthContext';
import PortalHeader from '../../components/Portal/PortalHeader';
import WaButton from '../../components/wa/WaButton';
import {
	bulkUpdateScheduleAssignments,
	generateTeacherSchedule,
	releaseScheduleRun,
} from '../../utils/teacherSchedulingService';
import WeekBoard from '../../components/Portal/Scheduling/WeekBoard';
import AttentionRail from '../../components/Portal/Scheduling/AttentionRail';
import CoverageDecision from '../../components/Portal/Scheduling/CoverageDecision';
import SchedulingDrawer from '../../components/Portal/Scheduling/SchedulingDrawer';
import {
	StudentRulesPanel,
	TimeOffPanel,
	AvailabilityPanel,
	LessonCapPanel,
} from '../../components/Portal/Scheduling/ManagePanels';
import {
	LOCATION_LABELS,
	addDays,
	buildAttentionItems,
	buildBlocks,
	buildRotationHistory,
	cleanBlocks,
	dateToDayKey,
	localTodayKey,
	formatDayKeyShort,
	formatWeekLabel,
	startOfWeek,
	teacherLoad,
	teacherCap,
	dayKeysBetween,
	weekDayKeys,
} from '../../components/Portal/Scheduling/schedulingModel';
import {
	nextScheduleRange,
	runRangeKeys,
	sortRunsNewestFirst,
} from '../../components/Portal/Scheduling/scheduleRuns';
import './TeacherScheduling.css';

/**
 * Teacher Scheduling — week board · attention rail · manage drawer.
 *
 * Rebuilt 2026-08-26 from the Claude Design mockup (portal card
 * "Rebuild the Teacher Scheduling page per the Design mockup"). Six stacked
 * sections became three layers ordered by touch frequency, so the
 * review-and-lock pass never leaves the board.
 *
 * SURFACE ONLY. Same collections, same callables, same engine: this page still
 * reads sourceEvents + scheduleAssignments and still writes through
 * updateScheduleAssignment / bulkUpdateScheduleAssignments. The rotation rules
 * did not change; what changed is that the page now SHOWS the judgments the
 * engine was already making.
 *
 * The derivations (what conflicts, what repeats, why a teacher is out) live in
 * components/Portal/Scheduling/schedulingModel.js so they can be tested without
 * Firestore. Read that file's header before touching time handling — these
 * events are stored wall-time-as-UTC and must NOT be formatted in a timezone.
 */

const RANGE_DAYS = 28;

const TeacherScheduling = () => {
	const { userProfile } = useAuth();
	const isAdmin = userProfile?.role === 'admin';
	const navigate = useNavigate();
	const deciderName = userProfile?.displayName || userProfile?.email || 'Admin';

	const todayKey = useMemo(() => dateToDayKey(new Date()), []);
	// "Has this day passed?" reads the LOCAL date; todayKey above is UTC and is
	// tomorrow after 7pm Central (schedulingModel.localTodayKey).
	const studioTodayKey = useMemo(() => localTodayKey(), []);

	// The generated RANGE — what Generate covers and what "Lock all clean" spans.
	const [rangeStart, setRangeStart] = useState(todayKey);
	const [rangeEnd, setRangeEnd] = useState(addDays(todayKey, RANGE_DAYS));

	/**
	 * The page OPENS ON THE NEWEST RUN'S OWN RANGE, and offers the next one.
	 *
	 * It used to open on today + 28 days whatever existed, so reviewing or
	 * changing the schedule meant re-typing its dates, refreshing and scrolling
	 * (Anthony, 2026-10-10). And the next schedule should start the day after
	 * the last released one ends ("I've only scheduled up until October 25, so
	 * ... the next start date should be October 26"): `nextRange` is that,
	 * offered as one button rather than applied silently, because the same
	 * page is also where the CURRENT schedule gets edited.
	 *
	 * `rangeReady` holds the first load until the runs are read, so the page
	 * does not load today's window and then immediately load the run's.
	 */
	const [rangeReady, setRangeReady] = useState(false);
	const [nextRange, setNextRange] = useState(null);
	// The loaded run's own range — Regenerate EXTENDS that run, so it is refused
	// for a range that lies wholly after it (that is a new schedule: Generate).
	const [runRange, setRunRange] = useState(null);
	const [streakWarnings, setStreakWarnings] = useState([]);

	// The visible WEEK — a window onto the range, never a separate query.
	const [focusDayKey, setFocusDayKey] = useState(startOfWeek(todayKey));

	/**
	 * Board scope: one WEEK, or the WHOLE generated RANGE.
	 *
	 * Everything else on this page was already range-grained — the block count,
	 * the load card, "Lock all clean" — while the board itself rendered seven
	 * days, so generating a month and being unable to see past its first week
	 * read as a broken page (Anthony, 2026-08-30).
	 *
	 * Orthogonal to review mode: condensed vs expanded is HOW MUCH each card
	 * says, scope is HOW MANY cards there are. "Condensed + whole range" is the
	 * month-at-a-glance that was asked for.
	 *
	 * DECLARED HERE, WITH THE OTHER BOARD-VIEW STATE, AND THAT POSITION IS
	 * LOAD-BEARING. It first shipped ~40 lines BELOW `visibleDayKeys`, which
	 * reads it — and that did NOT crash the way a const read inside its
	 * temporal dead zone should. Babel transpiles `const` to `var` for this
	 * project's browser targets, so the read yielded `undefined` rather than
	 * throwing, `undefined === 'range'` was false, and the board silently took
	 * the WEEK branch forever. The toggle and the status line sit further down
	 * the component and read the real value, so the page cheerfully said
	 * "Whole range … 0 flagged in range" above seven days of board.
	 *
	 * **A use-before-declare in a component does not fail loudly here — it
	 * fails as a wrong default**, which is the hardest kind to see. Keep every
	 * piece of view state above the memos that read it.
	 */
	const [scope, setScope] = useState('week');

	/**
	 * One switch that opens every day card at once.
	 *
	 * The per-card disclosure is still there and still works — this SETS them all
	 * rather than overriding them, so a card stays independently clickable after.
	 * Anthony, 2026-08-30: "I just want one toggle" instead of clicking through a
	 * month of cards to read who has what.
	 *
	 * Declared here with the other view state deliberately. See the scope comment
	 * above: state read by a memo further up this component reads `undefined`
	 * rather than throwing, and fails as a silent wrong default.
	 */
	const [showLessons, setShowLessons] = useState(false);
	const [reviewMode, setReviewMode] = useState(false);

	const [runId, setRunId] = useState('');
	// null | 'draft' | 'released' — legacy runs have no status and read as
	// released. Drives the Release button and the draft chip; the teacher view
	// (teacherViewData.fetchLatestSchedule) skips drafts entirely.
	const [runStatus, setRunStatus] = useState(null);
	const [teachers, setTeachers] = useState([]);
	const [rules, setRules] = useState([]);
	const [events, setEvents] = useState([]);
	const [assignments, setAssignments] = useState([]);
	const [timeOffEntries, setTimeOffEntries] = useState([]);
	const [coverageRequests, setCoverageRequests] = useState([]);

	const [blockDrafts, setBlockDrafts] = useState({});
	const [openDrawer, setOpenDrawer] = useState(null); // 'rules' | 'timeoff' | 'availability'
	const [activeCoverage, setActiveCoverage] = useState(null);

	const [loading, setLoading] = useState(false);
	const [busy, setBusy] = useState(false);
	const [savingRule, setSavingRule] = useState(false);
	const [savingTimeOff, setSavingTimeOff] = useState(false);
	const [savingCap, setSavingCap] = useState(false);
	const [message, setMessage] = useState(null);
	const [error, setError] = useState(null);

	/* ─────────────────────────── loading ─────────────────────────── */

	const loadTeachers = useCallback(async () => {
		const snapshot = await getDocs(query(collection(db, 'teachers'), where('active', '==', true)));
		// Caps live in schedulingConfig/teacherCaps, NOT on the teacher doc:
		// `teachers` is readable by anyone with viewSchedule (CalendarTab's
		// instructor picker needs it), and a cap is a staffing decision about a
		// person. Merged onto the teacher object here so every downstream
		// consumer — teacherCap, teacherLoad, ManagePanels, the load card —
		// keeps working against `maxLessons` unchanged. Read is admin/owner
		// only; this page is admin-gated, and a denial degrades to uncapped
		// rather than blanking the roster.
		let caps = {};
		try {
			const capsSnap = await getDoc(doc(db, 'schedulingConfig', 'teacherCaps'));
			caps = (capsSnap.exists() && capsSnap.data().caps) || {};
		} catch (err) {
			console.error('Lesson caps unreadable (treating everyone as uncapped):', err);
		}
		setTeachers(
			snapshot.docs.map((teacherDoc) => ({
				id: teacherDoc.id,
				...teacherDoc.data(),
				// `?? null` and never `||` — zero is a real cap.
				maxLessons: caps[teacherDoc.id] ?? null,
			}))
		);
	}, []);

	const loadRules = useCallback(async () => {
		const snapshot = await getDocs(collection(db, 'teacherSchedulingRules'));
		setRules(
			snapshot.docs
				.map((ruleDoc) => ({ id: ruleDoc.id, ...ruleDoc.data() }))
				.filter((rule) => rule.active !== false)
				.sort((a, b) => (a.studentMatch || '').localeCompare(b.studentMatch || ''))
		);
	}, []);

	const loadTimeOff = useCallback(async () => {
		const snapshot = await getDocs(query(collection(db, 'teacherTimeOff'), orderBy('date', 'asc')));
		setTimeOffEntries(snapshot.docs.map((entry) => ({ id: entry.id, ...entry.data() })));
	}, []);

	const loadCoverage = useCallback(async () => {
		const snapshot = await getDocs(
			query(collection(db, 'coverageRequests'), where('status', '==', 'open'))
		);
		setCoverageRequests(
			snapshot.docs
				.map((reqDoc) => ({ id: reqDoc.id, ...reqDoc.data() }))
				.sort((a, b) => (a.date < b.date ? -1 : 1))
		);
	}, []);

	const loadEvents = useCallback(async () => {
		// One week of lead-in so the visible week's rotation memory ("last wk")
		// is real rather than blank at the range's left edge.
		const from = new Date(`${addDays(rangeStart, -14)}T00:00:00Z`);
		const to = new Date(`${rangeEnd}T23:59:59Z`);
		const snapshot = await getDocs(
			query(
				collection(db, 'sourceEvents'),
				where('start', '>=', from),
				where('start', '<=', to),
				orderBy('start', 'asc')
			)
		);
		setEvents(snapshot.docs.map((eventDoc) => ({ id: eventDoc.id, ...eventDoc.data() })));
	}, [rangeStart, rangeEnd]);

	const loadAssignments = useCallback(async (currentRunId) => {
		if (!currentRunId) {
			setAssignments([]);
			setBlockDrafts({});
			return;
		}
		const snapshot = await getDocs(
			query(collection(db, 'scheduleAssignments'), where('runId', '==', currentRunId))
		);
		setAssignments(snapshot.docs.map((a) => ({ id: a.id, ...a.data() })));
		setBlockDrafts({});
	}, []);

	const loadLatestRunId = useCallback(async () => {
		const snapshot = await getDocs(
			query(collection(db, 'scheduleRuns'), orderBy('createdAt', 'desc'), limit(1))
		);
		return snapshot.empty ? '' : snapshot.docs[0].id;
	}, []);

	const loadRunStatus = useCallback(async (currentRunId) => {
		if (!currentRunId) {
			setRunStatus(null);
			return;
		}
		const snapshot = await getDoc(doc(db, 'scheduleRuns', currentRunId));
		setRunStatus(snapshot.exists() ? snapshot.data().status || null : null);
		setRunRange(snapshot.exists() ? runRangeKeys(snapshot.data()) : null);
	}, []);

	/** The runs list: sets the suggested next range, returns newest first. */
	const loadRuns = useCallback(async () => {
		const snapshot = await getDocs(
			query(collection(db, 'scheduleRuns'), orderBy('createdAt', 'desc'), limit(50))
		);
		const runs = sortRunsNewestFirst(snapshot.docs.map((runDoc) => ({ id: runDoc.id, ...runDoc.data() })));
		setNextRange(nextScheduleRange(runs));
		return runs;
	}, []);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				const runs = await loadRuns();
				if (cancelled) return;
				const { from, to } = runRangeKeys(runs[0]);
				if (from && to) {
					setRangeStart(from);
					setRangeEnd(to);
				}
			} catch (err) {
				console.error('Could not read schedule runs (opening on today):', err);
			} finally {
				if (!cancelled) setRangeReady(true);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [loadRuns]);

	const refresh = useCallback(
		async (currentRunId = runId) => {
			setLoading(true);
			setError(null);
			try {
				let effective = currentRunId;
				if (!effective) {
					effective = await loadLatestRunId();
					if (effective) setRunId(effective);
				}
				await Promise.all([
					loadTeachers(),
					loadRules(),
					loadTimeOff(),
					loadEvents(),
					loadCoverage(),
					loadAssignments(effective),
					loadRunStatus(effective),
				]);
			} catch (err) {
				console.error('Failed to load scheduling data:', err);
				setError(err.message || 'Failed to load scheduling data');
			} finally {
				setLoading(false);
			}
		},
		[runId, loadLatestRunId, loadTeachers, loadRules, loadTimeOff, loadEvents, loadCoverage, loadAssignments, loadRunStatus]
	);

	useEffect(() => {
		if (!rangeReady) return;
		refresh('');
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [rangeStart, rangeEnd, rangeReady]);

	/* ─────────────────────────── derivations ─────────────────────────── */

	const teachersById = useMemo(() => new Map(teachers.map((t) => [t.id, t])), [teachers]);
	const teacherNameById = useMemo(
		() => new Map(teachers.map((t) => [t.id, t.name || t.id])),
		[teachers]
	);

	const timeOffDates = useMemo(
		() => new Set(timeOffEntries.map((entry) => `${entry.teacherId}::${entry.date}`)),
		[timeOffEntries]
	);

	const assignmentsByEventId = useMemo(() => {
		const map = new Map();
		assignments.forEach((assignment) => map.set(assignment.sourceEventId, assignment));
		return map;
	}, [assignments]);

	/** Every block in the loaded window — the rotation history's source. */
	const allBlocks = useMemo(
		() => buildBlocks({ events, assignmentsByEventId }),
		[events, assignmentsByEventId]
	);

	const rotationHistory = useMemo(() => buildRotationHistory(allBlocks), [allBlocks]);

	/** Blocks inside the generated range — what "Lock all clean" spans. */
	const rangeBlocks = useMemo(
		() => allBlocks.filter((block) => block.dayKey >= rangeStart && block.dayKey <= rangeEnd),
		[allBlocks, rangeStart, rangeEnd]
	);

	const visibleDayKeys = useMemo(
		() => (scope === 'range' ? dayKeysBetween(rangeStart, rangeEnd) : weekDayKeys(focusDayKey)),
		[scope, rangeStart, rangeEnd, focusDayKey]
	);

	/**
	 * EVERY day in scope, empty ones included.
	 *
	 * The first version of range scope hid days with no lessons, on the theory
	 * that nine blank rows across a month were noise. Anthony's answer, and it
	 * is the right one: "Whole range" says every day, so a day silently missing
	 * from the list is worse than a quiet row — you cannot tell an empty Tuesday
	 * from a Tuesday the board decided not to draw. An empty row is also how you
	 * SEE that a day is empty, which is worth seeing while reviewing a month.
	 */
	const boardDayKeys = visibleDayKeys;

	/**
	 * Open on a week that actually has lessons.
	 *
	 * The range starts "today", but the fall term starts Aug 31 — so the first
	 * load showed seven empty day rows under a status bar reading "34 blocks in
	 * range", which reads as a broken page rather than an empty week (Anthony's
	 * screenshot, 2026-08-26). Runs once per load and only while the user is
	 * still on the week we defaulted them to, so it can never yank the board out
	 * from under someone who navigated deliberately.
	 */

	const [autoFocused, setAutoFocused] = useState(false);
	useEffect(() => {
		if (scope === 'range') return; // nothing to aim at — the whole range is shown
		if (autoFocused || loading || rangeBlocks.length === 0) return;
		const visible = new Set(visibleDayKeys);
		if (rangeBlocks.some((block) => visible.has(block.dayKey))) {
			setAutoFocused(true);
			return;
		}
		const firstWeek = startOfWeek(rangeBlocks[0].dayKey);
		setFocusDayKey(firstWeek);
		setAutoFocused(true);
	}, [autoFocused, loading, rangeBlocks, visibleDayKeys]);

	// A new range is a new question — let the board re-aim at it.
	useEffect(() => {
		setAutoFocused(false);
	}, [rangeStart, rangeEnd]);

	/** Blocks the board renders, with any unsaved draft applied on top. */
	const visibleBlocks = useMemo(() => {
		const visible = new Set(visibleDayKeys);
		return allBlocks
			.filter((block) => visible.has(block.dayKey))
			.map((block) => {
				const draft = blockDrafts[block.key];
				if (!draft) return block;
				return {
					...block,
					teacherId: draft.teacherId !== undefined ? draft.teacherId : block.teacherId,
					isLocked: draft.isLocked !== undefined ? draft.isLocked : block.isLocked,
					isMixed: draft.teacherId !== undefined ? false : block.isMixed,
				};
			});
	}, [allBlocks, visibleDayKeys, blockDrafts]);

	const railItems = useMemo(
		() =>
			buildAttentionItems({
				blocks: visibleBlocks,
				teachersById,
				timeOffDates,
				rotationHistory,
				todayKey: studioTodayKey,
			}),
		[visibleBlocks, teachersById, timeOffDates, rotationHistory, studioTodayKey]
	);

	const rangeClean = useMemo(
		() => cleanBlocks({ blocks: rangeBlocks, teachersById, timeOffDates, rotationHistory, todayKey: studioTodayKey }),
		[rangeBlocks, teachersById, timeOffDates, rotationHistory, studioTodayKey]
	);

	const lockableClean = useMemo(
		() => rangeClean.filter((block) => !block.isLocked),
		[rangeClean]
	);

	const loadRows = useMemo(
		() => teacherLoad({ blocks: rangeBlocks, teachers }),
		[rangeBlocks, teachers]
	);

	const pendingBlockKeys = useMemo(() => new Set(Object.keys(blockDrafts)), [blockDrafts]);

	const summaryText = useMemo(() => {
		const byDay = new Map();
		for (const block of rangeBlocks) {
			if (!byDay.has(block.dayKey)) byDay.set(block.dayKey, []);
			byDay
				.get(block.dayKey)
				.push(
					`${LOCATION_LABELS[block.location] || block.location} ${block.teacherId ? teacherNameById.get(block.teacherId) || block.teacherId : 'Unassigned'}`
				);
		}
		return [...byDay.entries()]
			.sort((a, b) => a[0].localeCompare(b[0]))
			.map(([dayKey, lines]) => [formatDayKeyShort(dayKey), ...lines].join('\n'))
			.join('\n\n');
	}, [rangeBlocks, teacherNameById]);

	/* ─────────────────────────── mutations ─────────────────────────── */

	const setDraft = (block, patch) => {
		setBlockDrafts((prev) => ({ ...prev, [block.key]: { ...(prev[block.key] || {}), ...patch } }));
	};

	const handleTeacherChange = (block, teacherId) => setDraft(block, { teacherId });
	const handleToggleLock = (block, isLocked) => setDraft(block, { isLocked });

	/** Apply a block's draft to every assignment it owns. */
	const applyBlock = async (block, draft) => {
		const assignmentIds = block.assignments.map((assignment) => assignment.id).filter(Boolean);
		if (assignmentIds.length === 0) {
			throw new Error('No assignments for this block yet — generate the schedule first.');
		}
		const base = allBlocks.find((candidate) => candidate.key === block.key) || block;
		await bulkUpdateScheduleAssignments({
			assignmentIds,
			teacherId: draft.teacherId !== undefined ? draft.teacherId : base.teacherId,
			isLocked: draft.isLocked !== undefined ? Boolean(draft.isLocked) : Boolean(base.isLocked),
		});
	};

	const handleSaveBlock = async (block) => {
		if (!isAdmin) return;
		const draft = blockDrafts[block.key];
		if (!draft) return;
		setBusy(true);
		setError(null);
		try {
			await applyBlock(block, draft);
			setMessage(`Saved ${formatDayKeyShort(block.dayKey)} · ${LOCATION_LABELS[block.location] || block.location}.`);
			await loadAssignments(runId);
		} catch (err) {
			console.error('Save block failed:', err);
			setError(err.message || 'Could not save that block.');
		} finally {
			setBusy(false);
		}
	};

	const handleSaveAll = async () => {
		if (!isAdmin) return;
		const keys = Object.keys(blockDrafts);
		if (keys.length === 0) {
			setMessage('No unsaved changes.');
			return;
		}
		setBusy(true);
		setError(null);
		try {
			const results = await Promise.allSettled(
				keys.map((key) => {
					const block = allBlocks.find((candidate) => candidate.key === key);
					if (!block) return Promise.resolve();
					return applyBlock(block, blockDrafts[key]);
				})
			);
			const failed = results.filter((result) => result.status === 'rejected');
			if (failed.length > 0) {
				setError(
					`${failed.length} block${failed.length === 1 ? '' : 's'} failed to save. First error: ${failed[0].reason?.message || 'unknown'}`
				);
			}
			setMessage(`Saved ${keys.length - failed.length} of ${keys.length} blocks.`);
			await loadAssignments(runId);
		} catch (err) {
			console.error('Save all failed:', err);
			setError(err.message || 'Could not save changes.');
		} finally {
			setBusy(false);
		}
	};

	const handleLockAllClean = async () => {
		if (!isAdmin || lockableClean.length === 0) return;
		setBusy(true);
		setError(null);
		try {
			const assignmentIds = lockableClean.flatMap((block) =>
				block.assignments.map((assignment) => assignment.id)
			);
			// Locking preserves each block's own teacher, so this cannot be one
			// bulk call — it is one per block, by design.
			const results = await Promise.allSettled(
				lockableClean.map((block) =>
					bulkUpdateScheduleAssignments({
						assignmentIds: block.assignments.map((assignment) => assignment.id),
						teacherId: block.teacherId,
						isLocked: true,
					})
				)
			);
			const failed = results.filter((result) => result.status === 'rejected').length;
			setMessage(
				`Locked ${lockableClean.length - failed} clean block${lockableClean.length - failed === 1 ? '' : 's'} (${assignmentIds.length} lessons) across ${rangeStart} → ${rangeEnd}.`
			);
			if (failed > 0) setError(`${failed} block${failed === 1 ? '' : 's'} could not be locked.`);
			await loadAssignments(runId);
		} catch (err) {
			console.error('Lock all clean failed:', err);
			setError(err.message || 'Could not lock the clean blocks.');
		} finally {
			setBusy(false);
		}
	};

	// Regenerate extends the loaded run to the chosen range; a range wholly
	// after that run is the NEXT schedule, and extending a released run into
	// it would change what teachers see live. That is Generate's job.
	const regenerateBlocked = Boolean(runRange?.to) && rangeStart > runRange.to;
	const onNextRange = Boolean(nextRange) && rangeStart === nextRange.from && rangeEnd === nextRange.to;

	const handleGenerate = async (keepLocked) => {
		if (!isAdmin) return;
		if (keepLocked && regenerateBlocked) return;
		setBusy(true);
		setError(null);
		setMessage(null);
		setStreakWarnings([]);
		try {
			const result = await generateTeacherSchedule({
				...(keepLocked && runId ? { runId } : {}),
				rangeStart: `${rangeStart}T00:00:00.000Z`,
				rangeEnd: `${rangeEnd}T23:59:59.999Z`,
			});
			const nextRunId = keepLocked && runId ? runId : result.runId;
			setRunId(nextRunId);
			setReviewMode(true);
			const backlog = result.backlog
				? ` Checked ${result.backlog.lessons} lesson${result.backlog.lessons === 1 ? '' : 's'} taught since ${formatDayKeyShort(result.backlog.from)} for repeat teachers.`
				: '';
			setMessage(
				(keepLocked
					? `Regenerated ${result.assignmentsUpdated ?? ''} assignments, locked rows preserved.`
					: `Generated a new schedule as a DRAFT. Teachers can't see it until you release it.`) + backlog
			);
			setStreakWarnings(Array.isArray(result.streakWarnings) ? result.streakWarnings : []);
			await Promise.all([refresh(nextRunId), loadRuns()]);
		} catch (err) {
			console.error('Generate failed:', err);
			setError(err.message || 'Schedule generation failed');
		} finally {
			setBusy(false);
		}
	};

	const handleRelease = async () => {
		// Handler-level guard, not just the disabled prop — a wa-* `disabled`
		// lands as a property after upgrade, so an early click would go through.
		if (!isAdmin || !runId || busy) return;
		setBusy(true);
		setError(null);
		setMessage(null);
		try {
			await releaseScheduleRun({ runId });
			setRunStatus('released');
			await loadRuns();
			setMessage('Schedule released — teachers now see this run.');
		} catch (err) {
			console.error('Release failed:', err);
			setError(err.message || 'Release failed');
		} finally {
			setBusy(false);
		}
	};

	/**
	 * Card note 1: "Sync calendar" was an MMS-era fossil — the fall pipeline
	 * feeds from timeSlots, not the ICS sync, so the old button refreshed a
	 * calendar that no longer carries the schedule. It is now "Refresh lessons"
	 * and calls the materializer, promoted from a migration script to an
	 * admin callable.
	 */
	/**
	 * Per-teacher lesson cap. Writes straight to the teachers doc: the rules
	 * already allow admin writes there (firestore.rules -> match /teachers), so
	 * this needs no callable, unlike teacherSchedulingRules/teacherTimeOff which
	 * are Functions-only.
	 *
	 * `null` clears the cap rather than writing 0 — those mean different things
	 * (uncapped vs. give her none), and deleteField() would leave the two
	 * indistinguishable from a reader's side.
	 */
	const handleSaveCap = async (teacher, nextCap) => {
		if (!isAdmin) return;
		setSavingCap(true);
		setError('');
		try {
			// Written to the private config doc, not the teacher doc. deleteField()
			// inside setDoc({merge:true}) removes the key entirely, so "uncapped"
			// is an ABSENT key rather than a stored null — which keeps `?? null`
			// on the read side honest and keeps zero distinguishable from unset.
			await setDoc(
				doc(db, 'schedulingConfig', 'teacherCaps'),
				{
					caps: { [teacher.id]: nextCap === null ? deleteField() : nextCap },
					updatedAt: serverTimestamp(),
				},
				{ merge: true }
			);
			await loadTeachers();
		} catch (err) {
			console.error('Save lesson cap failed:', err);
			setError(err.message || 'Could not save the lesson cap.');
		} finally {
			setSavingCap(false);
		}
	};

	const handleRefreshLessons = async () => {
		if (!isAdmin) return;
		setBusy(true);
		setError(null);
		setMessage(null);
		try {
			const result = await httpsCallable(functions, 'refreshLessonSourceEvents')({
				from: rangeStart,
				to: rangeEnd,
			});
			const data = result.data || {};
			setMessage(
				`Refreshed lessons ${rangeStart} → ${rangeEnd}: ${data.written ?? 0} occurrence${data.written === 1 ? '' : 's'} written, ${data.deleted ?? 0} stale removed, ${data.skippedTestStudents ?? 0} test-student slot${data.skippedTestStudents === 1 ? '' : 's'} skipped.`
			);
			await refresh(runId);
		} catch (err) {
			console.error('Refresh lessons failed:', err);
			setError(err.message || 'Could not refresh lessons from the slot grid.');
		} finally {
			setBusy(false);
		}
	};

	const handleCopySummary = async () => {
		if (!summaryText) {
			setError('Nothing to copy yet — generate a schedule first.');
			return;
		}
		try {
			await navigator.clipboard.writeText(summaryText);
			setMessage('Day/teacher summary copied.');
		} catch (err) {
			console.error('Copy failed:', err);
			setError('Copy failed — select the text in the summary drawer instead.');
		}
	};

	const handleDecideCoverage = async (request, decision, { covererId, note }) => {
		if (!isAdmin) return;
		setBusy(true);
		setError(null);
		try {
			await httpsCallable(functions, 'decideCoverageRequest')({
				requestId: request.id,
				decision,
				coveringTeacherId: covererId || null,
				note: (note || '').trim() || null,
			});
			setMessage(`Coverage request ${decision === 'approve' ? 'approved' : 'declined'}.`);
			setActiveCoverage(null);
			await Promise.all([loadCoverage(), loadAssignments(runId)]);
		} catch (err) {
			console.error('Coverage decision failed:', err);
			setError(err.message || 'Could not decide that request.');
		} finally {
			setBusy(false);
		}
	};

	const handleAddRule = async ({ studentMatch, blockedTeacherId, preferredTeacherId, scope }) => {
		if (!isAdmin) return false;
		const trimmed = (studentMatch || '').trim();
		if (!trimmed || !blockedTeacherId) {
			setError('A rule needs student text and a blocked teacher.');
			return false;
		}
		setSavingRule(true);
		setError(null);
		try {
			await addDoc(collection(db, 'teacherSchedulingRules'), {
				active: true,
				studentMatch: trimmed,
				blockedTeacherId,
				preferredTeacherId: preferredTeacherId || null,
				scope: scope === 'timeslot' ? 'timeslot' : 'day',
				createdAt: serverTimestamp(),
				updatedAt: serverTimestamp(),
				createdBy: userProfile?.email || null,
			});
			setMessage('Scheduling rule added.');
			await loadRules();
			return true;
		} catch (err) {
			console.error('Add rule failed:', err);
			setError(err.message || 'Failed to add rule');
			return false;
		} finally {
			setSavingRule(false);
		}
	};

	const handleRemoveRule = async (ruleId) => {
		if (!isAdmin) return;
		try {
			await updateDoc(doc(db, 'teacherSchedulingRules', ruleId), {
				active: false,
				updatedAt: serverTimestamp(),
			});
			setMessage('Scheduling rule removed.');
			await loadRules();
		} catch (err) {
			console.error('Remove rule failed:', err);
			setError(err.message || 'Failed to remove rule');
		}
	};

	const handleAddTimeOff = async ({ teacherId, startDate, endDate, startTime, endTime, reason }) => {
		if (!isAdmin) return false;
		if (!teacherId || !startDate) {
			setError('Time off needs a teacher and a start date.');
			return false;
		}
		if (Boolean(startTime) !== Boolean(endTime)) {
			setError('A timed window needs BOTH a from-time and an until-time (or neither).');
			return false;
		}
		setSavingTimeOff(true);
		setError(null);
		try {
			const result = await httpsCallable(functions, 'addTeacherTimeOff')({
				teacherId,
				startDate,
				endDate: endDate || startDate,
				reason: (reason || '').trim() || null,
				...(startTime ? { startTime, endTime } : {}),
			});
			const added = result.data?.added ?? result.data?.count ?? 0;
			setMessage(`Added ${added} time-off ${added === 1 ? 'entry' : 'entries'}.`);
			await Promise.all([loadTimeOff(), loadTeachers()]);
			return true;
		} catch (err) {
			console.error('Add time off failed:', err);
			setError(err.message || 'Failed to add time off');
			return false;
		} finally {
			setSavingTimeOff(false);
		}
	};

	const handleRemoveTimeOff = async (entryId) => {
		if (!isAdmin) return;
		try {
			await httpsCallable(functions, 'removeTeacherTimeOff')({ entryId });
			setMessage('Time off entry removed.');
			await loadTimeOff();
		} catch (err) {
			// The pre-rebuild page deleted the doc directly; fall back to that so a
			// legacy entry written before the callable existed can still be cleared.
			try {
				await deleteDoc(doc(db, 'teacherTimeOff', entryId));
				setMessage('Time off entry removed.');
				await loadTimeOff();
			} catch (fallbackErr) {
				console.error('Remove time off failed:', fallbackErr);
				setError(fallbackErr.message || 'Failed to remove time off');
			}
		}
	};

	const focusBlock = (block) => {
		setFocusDayKey(startOfWeek(block.dayKey));
		setReviewMode(false);
		const element = document.querySelector(`[data-block-key="${block.key}"]`);
		if (element) element.scrollIntoView({ block: 'center' });
	};

	/* ─────────────────────────── render ─────────────────────────── */

	const weekLabel = formatWeekLabel(focusDayKey);
	const isThisWeek = focusDayKey === startOfWeek(todayKey);

	return (
		<div className='ts-page'>
			<PortalHeader
				title='Teacher Scheduling'
				subtitle='Generate the rotation, review what needs a decision, lock the week'
			/>

			<div className='ts-shell'>
				<header className='ts-topbar'>
					<div className={`ts-weeknav${scope === 'range' ? ' is-rangescope' : ''}`}>
						<WaButton
							className='ts-weeknav__step'
							onClick={() => setFocusDayKey(addDays(focusDayKey, -7))}
							disabled={busy || scope === 'range'}>
							<span aria-hidden='true'>‹</span>
							<span className='ts-sr'>Previous week</span>
						</WaButton>
						<h2 className='ts-weeknav__label'>
							{scope === 'range' ? `${rangeStart} → ${rangeEnd}` : weekLabel}
						</h2>
						<WaButton
							className='ts-weeknav__step'
							onClick={() => setFocusDayKey(addDays(focusDayKey, 7))}
							disabled={busy || scope === 'range'}>
							<span aria-hidden='true'>›</span>
							<span className='ts-sr'>Next week</span>
						</WaButton>
						{!isThisWeek && (
							<WaButton
								className='ts-weeknav__today'
								appearance='plain'
								onClick={() => setFocusDayKey(startOfWeek(todayKey))}
								disabled={busy || scope === 'range'}>
								This week
							</WaButton>
						)}
					</div>

					<div className='ts-toolbar'>
						<label className='ts-range'>
							<span className='ts-sr'>Range start</span>
							<input
								type='date'
								value={rangeStart}
								onChange={(event) => setRangeStart(event.target.value)}
								disabled={busy}
							/>
						</label>
						<span className='ts-range__dash' aria-hidden='true'>
							–
						</span>
						<label className='ts-range'>
							<span className='ts-sr'>Range end</span>
							<input
								type='date'
								value={rangeEnd}
								onChange={(event) => setRangeEnd(event.target.value)}
								disabled={busy}
							/>
						</label>

						{nextRange && !onNextRange && (
							<WaButton
								onClick={() => {
									setRangeStart(nextRange.from);
									setRangeEnd(nextRange.to);
								}}
								disabled={busy}>
								Next schedule: {formatDayKeyShort(nextRange.from)} → {formatDayKeyShort(nextRange.to)}
							</WaButton>
						)}
						<WaButton onClick={handleRefreshLessons} disabled={!isAdmin || busy}>
							Refresh lessons
						</WaButton>
						<WaButton
							onClick={() => handleGenerate(true)}
							disabled={!isAdmin || !runId || busy || regenerateBlocked}
							title={regenerateBlocked ? 'This range starts after the loaded schedule ends. Use Generate for a new schedule.' : undefined}>
							Regenerate (keep locked)
						</WaButton>
						<WaButton
							onClick={() => {
								if (runId) navigate(`/portal/work/teacher-scheduling/print?run=${runId}`);
							}}
							disabled={!runId}>
							Print schedule
						</WaButton>
						{runStatus === 'draft' && (
							<WaButton
								className='ts-release'
								variant='brand'
								onClick={handleRelease}
								disabled={!isAdmin || busy}>
								Release to teachers
							</WaButton>
						)}
						<WaButton
							className='ts-generate'
							variant='brand'
							onClick={() => handleGenerate(false)}
							disabled={!isAdmin || busy}>
							{busy ? 'Working…' : 'Generate'}
						</WaButton>
					</div>
				</header>

				<div className='ts-statusbar'>
					<div className='ts-statusbar__facts'>
						{runId ? (
							<span className='ts-chip is-run'>Run {runId.slice(0, 8)}</span>
						) : (
							<span className='ts-chip is-warn'>No schedule generated yet</span>
						)}
						{runStatus === 'draft' && (
							<span className='ts-chip is-warn'>Draft — not visible to teachers</span>
						)}
						<span className='ts-statusbar__meta'>
							{rangeBlocks.length} block{rangeBlocks.length === 1 ? '' : 's'} in range ·{' '}
							{rangeClean.length} clean · {railItems.length} flagged{' '}
							{scope === 'range' ? 'in range' : 'this week'}
						</span>
					</div>
					<div className='ts-statusbar__actions'>
						{/* Scope and review mode are independent: this picks HOW MANY
						    cards, review mode picks how much each one says. */}
						<div className='ts-scope' role='group' aria-label='Board scope'>
							<button
								type='button'
								className={`ts-scope__btn${scope === 'week' ? ' is-active' : ''}`}
								aria-pressed={scope === 'week'}
								onClick={() => setScope('week')}>
								Week
							</button>
							<button
								type='button'
								className={`ts-scope__btn${scope === 'range' ? ' is-active' : ''}`}
								aria-pressed={scope === 'range'}
								onClick={() => setScope('range')}>
								Whole range
							</button>
						</div>
						<label className='ts-review'>
							<input
								type='checkbox'
								checked={showLessons}
								onChange={(event) => setShowLessons(event.target.checked)}
							/>
							<span>Show lessons</span>
						</label>
						<label className='ts-review'>
							<input
								type='checkbox'
								checked={reviewMode}
								onChange={(event) => setReviewMode(event.target.checked)}
							/>
							<span>Review mode — condense clean blocks</span>
						</label>
						<WaButton size='small' onClick={handleCopySummary} disabled={!summaryText}>
							Copy summary
						</WaButton>
						{pendingBlockKeys.size > 0 && (
							<WaButton
								size='small'
								variant='brand'
								onClick={handleSaveAll}
								disabled={!isAdmin || busy}>
								Save all ({pendingBlockKeys.size})
							</WaButton>
						)}
					</div>
				</div>

				{message && (
					<p className='ts-message is-success' role='status'>
						{message}
					</p>
				)}
				{/* Streaks the generator could not avoid (functions/rotationBacklog.js).
				    Listed by name so the review pass can hand-swap one; the engine
				    already preferred anyone else and nobody else was free. */}
				{streakWarnings.length > 0 && (
					<div className='ts-message is-warn' role='status'>
						<p className='ts-message__title'>
							{streakWarnings.length} lesson{streakWarnings.length === 1 ? '' : 's'} keep a student with the same
							teacher. No one else was free, so these may need a hand swap:
						</p>
						<ul className='ts-message__list'>
							{streakWarnings.map((w) => (
								<li key={`${w.date}-${w.studentId}`}>
									{formatDayKeyShort(w.date)} · {w.studentName} with {w.teacherName || 'the same teacher'}: {w.reason}
								</li>
							))}
						</ul>
					</div>
				)}
				{error && (
					<p className='ts-message is-error' role='alert'>
						{error}
					</p>
				)}

				{activeCoverage && (
					<CoverageDecision
						request={activeCoverage}
						teachers={teachers}
						teacherNameById={teacherNameById}
						blocks={allBlocks}
						timeOffDates={timeOffDates}
						deciderName={deciderName}
						busy={busy}
						onDecide={handleDecideCoverage}
						onCancel={() => setActiveCoverage(null)}
					/>
				)}

				<div className='ts-main'>
					{loading ? (
						<div className='ts-loading'>
							<wa-spinner />
						</div>
					) : (
						<WeekBoard
							dayKeys={boardDayKeys}
							groupByWeek={scope === 'range'}
							expandLessons={showLessons}
							blocks={visibleBlocks}
							teachers={teachers}
							teachersById={teachersById}
							teacherNameById={teacherNameById}
							timeOffDates={timeOffDates}
							rotationHistory={rotationHistory}
							todayKey={studioTodayKey}
							condensed={reviewMode}
							isAdmin={isAdmin}
							busy={busy}
							pendingBlockKeys={pendingBlockKeys}
							onTeacherChange={handleTeacherChange}
							onToggleLock={handleToggleLock}
							onSaveBlock={handleSaveBlock}
						/>
					)}

					<AttentionRail
						items={railItems}
						coverageRequests={coverageRequests}
						teacherNameById={teacherNameById}
						cleanCount={lockableClean.length}
						lockScopeLabel={`${rangeStart} → ${rangeEnd}`}
						isAdmin={isAdmin}
						busy={busy}
						onLockAllClean={handleLockAllClean}
						onFocusBlock={focusBlock}
						onOpenCoverage={(request) => setActiveCoverage(request)}
						loadRows={loadRows}
					/>
				</div>

				<footer className='ts-manage'>
					<button type='button' className='ts-manage__card' onClick={() => setOpenDrawer('rules')}>
						<span className='ts-manage__title'>Student rules</span>
						<span className='ts-manage__meta'>
							{rules.length} active {rules.length === 1 ? 'rule' : 'rules'}
						</span>
					</button>
					<button type='button' className='ts-manage__card' onClick={() => setOpenDrawer('timeoff')}>
						<span className='ts-manage__title'>Time off</span>
						<span className='ts-manage__meta'>
							{timeOffEntries.length} {timeOffEntries.length === 1 ? 'entry' : 'entries'}
						</span>
					</button>
					<button type='button' className='ts-manage__card' onClick={() => setOpenDrawer('caps')}>
						<span className='ts-manage__title'>Lesson caps</span>
						<span className='ts-manage__meta'>
							{teachers.filter((teacher) => teacherCap(teacher) !== null).length} of{' '}
							{teachers.length} capped
						</span>
					</button>
					<button
						type='button'
						className='ts-manage__card'
						onClick={() => setOpenDrawer('availability')}>
						<span className='ts-manage__title'>Weekly availability</span>
						<span className='ts-manage__meta'>
							{teachers.filter((teacher) => teacher.availabilityUpdatedAt).length} of{' '}
							{teachers.length} entered
						</span>
					</button>
				</footer>
			</div>

			<SchedulingDrawer
				open={openDrawer === 'rules'}
				label='Student rules'
				onClose={() => setOpenDrawer(null)}>
				<StudentRulesPanel
					rules={rules}
					teachers={teachers}
					teacherNameById={teacherNameById}
					isAdmin={isAdmin}
					saving={savingRule}
					onAdd={handleAddRule}
					onRemove={handleRemoveRule}
				/>
			</SchedulingDrawer>

			<SchedulingDrawer
				open={openDrawer === 'timeoff'}
				label='Teacher time off'
				onClose={() => setOpenDrawer(null)}>
				<TimeOffPanel
					entries={timeOffEntries}
					teachers={teachers}
					teacherNameById={teacherNameById}
					isAdmin={isAdmin}
					saving={savingTimeOff}
					onAdd={handleAddTimeOff}
					onRemove={handleRemoveTimeOff}
				/>
			</SchedulingDrawer>

			<SchedulingDrawer
				open={openDrawer === 'caps'}
				label='Lesson caps'
				onClose={() => setOpenDrawer(null)}>
				<LessonCapPanel
					teachers={teachers}
					isAdmin={isAdmin}
					saving={savingCap}
					onSave={handleSaveCap}
				/>
			</SchedulingDrawer>

			<SchedulingDrawer
				open={openDrawer === 'availability'}
				label='Weekly availability'
				onClose={() => setOpenDrawer(null)}>
				<AvailabilityPanel
					teachers={teachers}
					teacherNameById={teacherNameById}
					onChanged={() => {
						loadTeachers();
						loadTimeOff();
					}}
				/>
			</SchedulingDrawer>
		</div>
	);
};

export default TeacherScheduling;
